import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { action } from "@ember/object";
import { service } from "@ember/service";
import { LatencyTester } from "../lib/latency-tester";
import { ajax } from "discourse/lib/ajax";
import { popupAjaxError } from "discourse/lib/ajax-error";
import { on } from "@ember/modifier";
import { fn } from "@ember/helper";
import eq from "truth-helpers/helpers/eq";
import not from "truth-helpers/helpers/not";
import and from "truth-helpers/helpers/and";

function grade(ms) {
  if (ms === null || ms === undefined) return { letter: "—", cls: "" };
  if (ms < 200) return { letter: "A", cls: "pd-live-benchmark__grade--a" };
  if (ms < 500) return { letter: "B", cls: "pd-live-benchmark__grade--b" };
  if (ms < 1000) return { letter: "C", cls: "pd-live-benchmark__grade--c" };
  if (ms < 2000) return { letter: "D", cls: "pd-live-benchmark__grade--d" };
  return { letter: "F", cls: "pd-live-benchmark__grade--f" };
}

function latencyComparisonClass(avg, adminAvg) {
  if (!adminAvg) return "";
  if (avg < adminAvg * 0.8) return "pd-live-benchmark__compare--better";
  if (avg > adminAvg * 1.2) return "pd-live-benchmark__compare--worse";
  return "pd-live-benchmark__compare--similar";
}

function latencyDelta(avg, adminAvg) {
  if (!adminAvg) return "";
  const diff = avg - adminAvg;
  const sign = diff >= 0 ? "+" : "";
  return `${sign}${Math.round(diff)}ms`;
}

export default class LiveBenchmarkRunner extends Component {
  @service currentUser;

  @tracked isTesting = false;
  @tracked progress = 0;
  @tracked results = null;
  // "Global" (capital) to match REGIONS in BenchmarkRun — the lowercase
  // default failed the model's `inclusion` validation on first submit.
  @tracked selectedRegion = "Global";
  @tracked error = null;
  @tracked hasSubmitted = false;

  get regions() {
    return [
      { id: "Global", name: "Global / Default" },
      { id: "US-East", name: "US East (N. Virginia)" },
      { id: "US-West", name: "US West (Oregon)" },
      { id: "EU-Central", name: "EU Central (Frankfurt)" },
      { id: "APAC", name: "Asia Pacific (Singapore)" },
    ];
  }

  get provider() {
    return this.args.provider;
  }

  get latestAdminRun() {
    return this.args.latestAdminRun || null;
  }

  get gradeInfo() {
    if (!this.results) return null;
    return grade(this.results.avg);
  }

  get comparisonInfo() {
    if (!this.results || !this.latestAdminRun) return null;
    const adminAvg = this.latestAdminRun.avg_response_time_ms;
    return {
      adminAvg,
      class: latencyComparisonClass(this.results.avg, adminAvg),
      delta: latencyDelta(this.results.avg, adminAvg),
    };
  }

  get canSubmit() {
    return this.currentUser && this.results && this.results.avg != null && !this.hasSubmitted;
  }

  @action
  selectRegion(e) {
    this.selectedRegion = e.target.value;
  }

  @action
  async runTest() {
    if (this.isTesting) return;
    if (!this.provider) {
      this.error = "Provider data not loaded yet. Please try again.";
      return;
    }

    this.isTesting = true;
    this.progress = 0;
    this.results = null;
    this.error = null;
    this.hasSubmitted = false;

    let progressInterval = null;
    try {
      let endpoint = this.provider?.website_url;
      if (
        this.provider?.ping_endpoints &&
        Object.keys(this.provider.ping_endpoints).length > 0
      ) {
        endpoint =
          this.provider.ping_endpoints[this.selectedRegion] ||
          this.provider.ping_endpoints["Global"] ||
          endpoint;
      }

      if (!endpoint) {
        throw new Error(
          "No valid endpoint configured for testing this provider."
        );
      }

      progressInterval = setInterval(() => {
        if (this.progress < 90) {
          this.progress += 5;
        }
      }, 300);

      const stats = await LatencyTester.runSuite(endpoint, 10, 200);

      clearInterval(progressInterval);
      this.progress = 100;

      if (stats.error) {
        throw new Error(stats.error);
      }

      this.results = {
        ...stats,
        region: this.selectedRegion,
        endpoint,
      };
    } catch (err) {
      this.error = err.message || "Failed to complete benchmark test.";
    } finally {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      this.isTesting = false;
    }
  }

  @action
  async submitResults() {
    if (!this.canSubmit) return;

    try {
      const successCount = Math.round(10 * (this.results.successRate / 100));

      await ajax("/benchmarks/submit", {
        type: "POST",
        data: {
          benchmark_run: {
            provider_id: this.provider.id,
            test_type: "speed",
            region: this.selectedRegion,
            protocol: "https",
            concurrent_connections: 1,
            total_requests: 10,
            successful_requests: successCount,
            failed_requests: 10 - successCount,
            avg_response_time_ms: this.results.avg,
            block_rate: 100 - this.results.successRate,
          },
        },
      });

      this.hasSubmitted = true;
    } catch (err) {
      popupAjaxError(err);
    }
  }

  <template>
    <div class="pd-live-benchmark-runner">
      <div class="pd-live-benchmark__header">
        <h3>Live Browser Test</h3>
        <p>Run a real-time latency test from your browser directly to
          <strong>{{this.provider.name}}</strong>'s test endpoints.</p>
      </div>

      <div class="pd-live-benchmark__controls">
        <label class="pd-live-benchmark__region-label">
          Test Region:
          <select {{on "change" this.selectRegion}} disabled={{this.isTesting}}>
            {{#each this.regions as |region|}}
              <option value={{region.id}} selected={{eq this.selectedRegion region.id}}>{{region.name}}</option>
            {{/each}}
          </select>
        </label>

        <button type="button" class="btn btn-primary pd-live-benchmark__start-btn" disabled={{this.isTesting}} {{on "click" this.runTest}}>
          {{if this.isTesting "Running Test..." "Start Test"}}
        </button>
      </div>

      {{#if this.isTesting}}
        <div class="pd-live-benchmark__progress">
          <div class="pd-live-benchmark__progress-bar-bg">
            <div class="pd-live-benchmark__progress-bar-fill" style="width: {{this.progress}}%;"></div>
          </div>
          <span class="pd-live-benchmark__progress-label">Testing {{this.provider.name}}... ({{this.progress}}%)</span>
        </div>
      {{/if}}

      {{#if this.error}}
        <div class="pd-live-benchmark__error">
          {{this.error}}
        </div>
      {{/if}}

      {{#if this.results}}
        <div class="pd-live-benchmark__results">
          <div class="pd-live-benchmark__results-header">
            <h4>Test Complete!</h4>
            {{#if this.gradeInfo}}
              <span class="pd-live-benchmark__grade {{this.gradeInfo.cls}}">{{this.gradeInfo.letter}}</span>
            {{/if}}
          </div>

          <div class="pd-live-benchmark__stats-grid">
            <div class="pd-live-benchmark__stat-box">
              <span class="pd-live-benchmark__stat-label">Avg Latency</span>
              <strong class="pd-live-benchmark__stat-value pd-live-benchmark__stat-value--primary">{{this.results.avg}} ms</strong>
            </div>
            <div class="pd-live-benchmark__stat-box">
              <span class="pd-live-benchmark__stat-label">Min Latency</span>
              <strong class="pd-live-benchmark__stat-value">{{this.results.min}} ms</strong>
            </div>
            <div class="pd-live-benchmark__stat-box">
              <span class="pd-live-benchmark__stat-label">Jitter</span>
              <strong class="pd-live-benchmark__stat-value">{{this.results.jitter}} ms</strong>
            </div>
            <div class="pd-live-benchmark__stat-box">
              <span class="pd-live-benchmark__stat-label">Success Rate</span>
              <strong class="pd-live-benchmark__stat-value">{{this.results.successRate}}%</strong>
            </div>
          </div>

          {{#if this.comparisonInfo}}
            <div class="pd-live-benchmark__comparison {{this.comparisonInfo.class}}">
              <span class="pd-live-benchmark__comparison-label">vs. Admin Average ({{this.comparisonInfo.adminAvg}}ms):</span>
              <span class="pd-live-benchmark__comparison-delta">{{this.comparisonInfo.delta}}</span>
            </div>
          {{/if}}

          <div class="pd-live-benchmark__submit-action">
            {{#if this.hasSubmitted}}
              <span class="pd-live-benchmark__success-msg">✓ Results submitted to community benchmarks!</span>
            {{else}}
              <span class="pd-live-benchmark__submit-hint">Help the community by submitting your results anonymously.</span>
              <button type="button" class="btn btn-default pd-live-benchmark__submit-btn" disabled={{not this.canSubmit}} {{on "click" this.submitResults}}>
                Submit Results
              </button>
            {{/if}}
          </div>
        </div>
      {{/if}}
    </div>
  </template>
}
